import React, { useState } from 'react';
import { CheckCircle, XCircle, Clock, AlertCircle, User, Building2, CreditCard, Calendar } from 'lucide-react';
import { format } from 'date-fns';

const STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'text-yellow-400', bg: 'bg-yellow-500/20 border-yellow-500/30', icon: Clock },
  approved: { label: 'Approved', color: 'text-blue-400', bg: 'bg-blue-500/20 border-blue-500/30', icon: CheckCircle },
  rejected: { label: 'Rejected', color: 'text-rose-400', bg: 'bg-rose-500/20 border-rose-500/30', icon: XCircle },
  paid: { label: 'Paid', color: 'text-emerald-400', bg: 'bg-emerald-500/20 border-emerald-500/30', icon: CheckCircle },
};

const AdminApprovalPanel = ({ payoutRequests, loading, currency, onApprove, onReject, isAdmin }) => {
  const [processingId, setProcessingId] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [rejectingId, setRejectingId] = useState(null);
  const [error, setError] = useState('');

  const formatAmount = (val) => new Intl.NumberFormat('en-KE', { minimumFractionDigits: 2 })?.format(val || 0);
  const formatDate = (dateStr) => {
    try { return format(new Date(dateStr), 'MMM d, yyyy · h:mm a'); }
    catch { return dateStr || '—'; }
  };

  const handleApprove = async (requestId) => {
    setProcessingId(requestId);
    setError('');
    try {
      await onApprove?.(requestId);
    } catch (err) {
      setError(err?.message || 'Failed to approve payout');
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (requestId) => {
    if (!rejectionReason?.trim()) {
      setError('Please provide a rejection reason');
      return;
    }
    setProcessingId(requestId);
    setError('');
    try {
      await onReject?.(requestId, rejectionReason);
      setRejectingId(null);
      setRejectionReason('');
    } catch (err) {
      setError(err?.message || 'Failed to reject payout');
    } finally {
      setProcessingId(null);
    }
  };

  const pendingCount = (payoutRequests || [])?.filter(r => r?.status === 'pending')?.length;

  return (
    <div className="bg-[#0d1f3c] rounded-2xl border border-white/10 overflow-hidden">
      {/* Header */}
      <div className="p-5 border-b border-white/10 flex items-center justify-between">
        <div>
          <h3 className="text-white font-semibold text-lg">Payout Requests</h3>
          <p className="text-white/40 text-xs mt-0.5">
            {isAdmin ? 'Admin approval panel' : 'Your withdrawal requests'}
          </p>
        </div>
        {pendingCount > 0 && (
          <span className="px-3 py-1 bg-yellow-500/20 border border-yellow-500/30 text-yellow-400 text-xs font-semibold rounded-full">
            {pendingCount} pending
          </span>
        )}
      </div>
      {error && (
        <div className="mx-5 mt-4 flex items-center gap-2 bg-rose-500/20 border border-rose-500/30 rounded-xl p-3">
          <AlertCircle className="w-4 h-4 text-rose-400 flex-shrink-0" />
          <p className="text-rose-300 text-sm">{error}</p>
        </div>
      )}
      {/* List */}
      <div className="divide-y divide-white/5">
        {loading ? (
          Array.from({ length: 3 })?.map((_, i) => (
            <div key={i} className="p-5 animate-pulse space-y-3">
              <div className="flex justify-between">
                <div className="h-4 w-32 bg-white/10 rounded" />
                <div className="h-4 w-20 bg-white/10 rounded" />
              </div>
              <div className="h-3 w-48 bg-white/5 rounded" />
            </div>
          ))
        ) : (payoutRequests || [])?.length === 0 ? (
          <div className="p-12 text-center">
            <p className="text-white/30 text-sm">No payout requests</p>
          </div>
        ) : (
          (payoutRequests || [])?.map(req => {
            const statusCfg = STATUS_CONFIG?.[req?.status] || STATUS_CONFIG?.pending;
            const StatusIcon = statusCfg?.icon;
            const isProcessing = processingId === req?.id;
            const isRejectMode = rejectingId === req?.id;

            return (
              <div key={req?.id} className="p-5">
                {/* Top row */}
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <p className="text-white font-semibold text-lg">
                        {currency || 'KES'} {formatAmount(req?.amount)}
                      </p>
                      <span className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${statusCfg?.bg} ${statusCfg?.color}`}>
                        {StatusIcon && <StatusIcon className="w-3 h-3" aria-hidden="true" />}
                        {statusCfg?.label}
                      </span>
                    </div>
                    <div className="flex items-center gap-1 text-white/40 text-xs">
                      <Calendar className="w-3 h-3" />
                      {formatDate(req?.created_at)}
                    </div>
                  </div>
                  <p className="text-white/20 text-xs">{req?.id?.slice(0, 8)}...</p>
                </div>
                {/* Bank details */}
                <div className="grid grid-cols-3 gap-3 mb-4">
                  <div className="bg-white/5 rounded-lg p-2.5">
                    <div className="flex items-center gap-1.5 mb-1">
                      <Building2 className="w-3 h-3 text-white/30" />
                      <span className="text-white/30 text-xs">Bank</span>
                    </div>
                    <p className="text-white/70 text-xs font-medium">{req?.bank_name || '—'}</p>
                  </div>
                  <div className="bg-white/5 rounded-lg p-2.5">
                    <div className="flex items-center gap-1.5 mb-1">
                      <CreditCard className="w-3 h-3 text-white/30" />
                      <span className="text-white/30 text-xs">Account</span>
                    </div>
                    <p className="text-white/70 text-xs font-medium">{req?.account_number || '—'}</p>
                  </div>
                  <div className="bg-white/5 rounded-lg p-2.5">
                    <div className="flex items-center gap-1.5 mb-1">
                      <User className="w-3 h-3 text-white/30" />
                      <span className="text-white/30 text-xs">Name</span>
                    </div>
                    <p className="text-white/70 text-xs font-medium truncate">{req?.account_name || '—'}</p>
                  </div>
                </div>
                {/* Rejection reason input */}
                {isRejectMode && (
                  <div className="mb-3">
                    <input
                      type="text"
                      value={rejectionReason}
                      onChange={e => setRejectionReason(e?.target?.value)}
                      placeholder="Reason for rejection..."
                      className="w-full bg-white/5 border border-rose-500/30 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-rose-500/60 placeholder-white/20"
                    />
                  </div>
                )}
                {/* Rejection reason display */}
                {req?.rejection_reason && (
                  <div className="mb-3 bg-rose-500/10 border border-rose-500/20 rounded-lg p-2.5">
                    <p className="text-rose-300/70 text-xs">Rejection reason: {req?.rejection_reason}</p>
                  </div>
                )}
                {/* Admin actions */}
                {isAdmin && req?.status === 'pending' && (
                  <div className="flex gap-2">
                    {isRejectMode ? (
                      <>
                        <button
                          onClick={() => handleReject(req?.id)}
                          disabled={isProcessing}
                          className="btn-danger flex-1 disabled:opacity-50"
                        >
                          {isProcessing ? 'Rejecting...' : 'Confirm Reject'}
                        </button>
                        <button
                          onClick={() => { setRejectingId(null); setRejectionReason(''); }}
                          className="btn-secondary px-4"
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => handleApprove(req?.id)}
                          disabled={isProcessing}
                          className="btn-primary flex-1 disabled:opacity-50"
                        >
                          {isProcessing ? (
                            <div className="w-3.5 h-3.5 border-2 border-[#0a1628]/30 border-t-[#0a1628] rounded-full animate-spin" />
                          ) : (
                            <CheckCircle className="w-3.5 h-3.5" />
                          )}
                          Approve & Pay
                        </button>
                        <button
                          onClick={() => setRejectingId(req?.id)}
                          disabled={isProcessing}
                          className="btn-danger flex-1 disabled:opacity-50"
                        >
                          <XCircle className="w-3.5 h-3.5" />
                          Reject
                        </button>
                      </>
                    )}
                  </div>
                )}
                {req?.processed_at && (
                  <p className="text-white/20 text-xs mt-2">
                    Processed: {formatDate(req?.processed_at)}
                  </p>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default AdminApprovalPanel;
